import mammoth from 'mammoth';
import JSZip from 'jszip';

// WordprocessingML + DrawingML(theme) namespaces
const W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const A = 'http://schemas.openxmlformats.org/drawingml/2006/main';

// Word's named highlight colors -> hex
const HIGHLIGHT_COLORS = {
  yellow: '#ffff00', green: '#00ff00', cyan: '#00ffff', magenta: '#ff00ff',
  blue: '#0000ff', red: '#ff0000', darkBlue: '#000080', darkCyan: '#008080',
  darkGreen: '#008000', darkMagenta: '#800080', darkRed: '#800000',
  darkYellow: '#808000', darkGray: '#808080', lightGray: '#c0c0c0',
  black: '#000000', white: '#ffffff',
};

// Word themeColor attribute values -> theme color-scheme slot names
const THEME_SLOT = {
  dark1: 'dk1', text1: 'dk1', dark2: 'dk2', text2: 'dk2',
  light1: 'lt1', background1: 'lt1', light2: 'lt2', background2: 'lt2',
  accent1: 'accent1', accent2: 'accent2', accent3: 'accent3',
  accent4: 'accent4', accent5: 'accent5', accent6: 'accent6',
  hyperlink: 'hlink', followedHyperlink: 'folHlink',
};

// Convert a .docx ArrayBuffer to clean HTML.
// mammoth handles headings/bold/italic/lists/tables/links, but it drops a lot
// of visual formatting (text color, highlight, alignment, table cell shading).
// We read those from the raw docx XML — resolving colors from direct runs,
// paragraph/character styles, AND the theme — then re-apply them.
export async function convertDocxToHtml(arrayBuffer) {
  const result = await mammoth.convertToHtml({ arrayBuffer });
  let html = result.value;

  try {
    const { paragraphs, tables } = await extractFormatting(arrayBuffer);
    html = applyFormatting(html, paragraphs, tables);
  } catch (err) {
    // If anything fails, keep mammoth's plain output rather than breaking.
    console.log('docx formatting extract skipped =>>', err);
  }

  return { html, messages: result.messages };
}

/* ----------------------------- XML extraction ----------------------------- */

async function extractFormatting(arrayBuffer) {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const xmlFile = zip.file('word/document.xml');
  if (!xmlFile) return { paragraphs: [], tables: [] };

  const parse = (s) => new DOMParser().parseFromString(s, 'application/xml');
  const doc = parse(await xmlFile.async('string'));

  const stylesFile = zip.file('word/styles.xml');
  const themeFile = zip.file('word/theme/theme1.xml');
  const stylesDoc = stylesFile ? parse(await stylesFile.async('string')) : null;
  const themeDoc = themeFile ? parse(await themeFile.async('string')) : null;

  const ctx = buildContext(stylesDoc, themeDoc);

  // Body-level paragraphs (not inside a table), in document order.
  const allParas = doc.getElementsByTagNameNS(W, 'p');
  const paragraphs = [];
  for (let i = 0; i < allParas.length; i++) {
    if (isInsideTable(allParas[i])) continue;
    paragraphs.push(getParagraphInfo(allParas[i], ctx));
  }

  // Tables -> rows -> cells
  const tblEls = doc.getElementsByTagNameNS(W, 'tbl');
  const tables = [];
  for (let t = 0; t < tblEls.length; t++) {
    const rowEls = tblEls[t].getElementsByTagNameNS(W, 'tr');
    const rows = [];
    for (let r = 0; r < rowEls.length; r++) {
      const cellEls = rowEls[r].getElementsByTagNameNS(W, 'tc');
      const cells = [];
      for (let c = 0; c < cellEls.length; c++) {
        const tc = cellEls[c];
        // Skip vertically-merged continuation cells (mammoth omits them),
        // so cell indexes stay aligned with the generated <td> order.
        const vMerge = firstChildNS(firstChildNS(tc, 'tcPr'), 'vMerge');
        if (vMerge) {
          const val = vMerge.getAttributeNS(W, 'val');
          if (!val || val === 'continue') continue;
        }
        cells.push(getCellInfo(tc, ctx));
      }
      rows.push(cells);
    }
    tables.push(rows);
  }

  return { paragraphs, tables };
}

function getParagraphInfo(p, ctx) {
  const pStyleColor = ctx.resolveStyleColor(pStyleId(p));
  const runInfo = analyzeRuns(p.getElementsByTagNameNS(W, 'r'), ctx, pStyleColor);
  return {
    text: normalize(getText(p)),
    align: getAlign(p),
    color: runInfo.color,
    highlight: runInfo.highlight,
  };
}

function getCellInfo(tc, ctx) {
  const shd = firstChildNS(firstChildNS(tc, 'tcPr'), 'shd');
  let fill = null;
  if (shd) {
    const f = shd.getAttributeNS(W, 'fill');
    if (f && f.toLowerCase() !== 'auto') fill = '#' + f;
  }
  const firstPara = firstChildNS(tc, 'p');
  const pStyleColor = firstPara ? ctx.resolveStyleColor(pStyleId(firstPara)) : null;
  const runInfo = analyzeRuns(tc.getElementsByTagNameNS(W, 'r'), ctx, pStyleColor);
  return {
    fill,
    color: runInfo.color,
    highlight: runInfo.highlight,
    align: firstPara ? getAlign(firstPara) : null,
  };
}

// Uniform color/highlight across a set of runs (null if mixed).
function analyzeRuns(runs, ctx, paraStyleColor) {
  let color = null, colorSet = false, colorOk = true;
  let hl = null, hlSet = false, hlOk = true;

  for (let i = 0; i < runs.length; i++) {
    const run = runs[i];
    if (!getText(run).trim()) continue; // ignore empty / whitespace-only runs

    const c = effectiveRunColor(run, ctx, paraStyleColor);
    if (!colorSet) { color = c; colorSet = true; } else if (color !== c) colorOk = false;

    const rPr = firstChildNS(run, 'rPr');
    const hEl = firstChildNS(rPr, 'highlight');
    const hv = hEl ? hEl.getAttributeNS(W, 'val') : null;
    const h = (hv && hv !== 'none') ? (HIGHLIGHT_COLORS[hv] || null) : null;
    if (!hlSet) { hl = h; hlSet = true; } else if (hl !== h) hlOk = false;
  }

  return {
    color: colorOk ? color : null,
    highlight: hlOk ? hl : null,
  };
}

// Effective color of a run: direct run color -> run's char style -> paragraph
// style color. (docDefaults/black is intentionally ignored so we don't force
// plain text to hard black.)
function effectiveRunColor(run, ctx, paraStyleColor) {
  const rPr = firstChildNS(run, 'rPr');
  const direct = colorFromRPr(rPr, ctx);
  if (direct) return direct;

  const rStyle = valOf(firstChildNS(rPr, 'rStyle'));
  if (rStyle) {
    const c = ctx.resolveStyleColor(rStyle);
    if (c) return c;
  }
  return paraStyleColor || null;
}

/* --------------------------- styles + theme ctx --------------------------- */

function buildContext(stylesDoc, themeDoc) {
  // Theme color scheme: slot name -> hex (no '#')
  const themeColors = {};
  if (themeDoc) {
    const scheme = themeDoc.getElementsByTagNameNS(A, 'clrScheme')[0];
    if (scheme) {
      for (let i = 0; i < scheme.childNodes.length; i++) {
        const slot = scheme.childNodes[i];
        if (slot.nodeType !== 1) continue;
        const srgb = firstChildNSns(slot, A, 'srgbClr');
        const sys = firstChildNSns(slot, A, 'sysClr');
        let hex = null;
        if (srgb) hex = srgb.getAttributeNS(A, 'val') || srgb.getAttribute('val');
        else if (sys) hex = sys.getAttributeNS(A, 'lastClr') || sys.getAttribute('lastClr');
        if (hex) themeColors[slot.localName] = hex;
      }
    }
  }

  // Style definitions: styleId -> { rPr, basedOn }
  const styleMap = {};
  if (stylesDoc) {
    const styles = stylesDoc.getElementsByTagNameNS(W, 'style');
    for (let i = 0; i < styles.length; i++) {
      const id = styles[i].getAttributeNS(W, 'styleId');
      if (!id) continue;
      styleMap[id] = {
        rPr: firstChildNS(styles[i], 'rPr'),
        basedOn: valOf(firstChildNS(styles[i], 'basedOn')),
      };
    }
  }

  const ctx = { themeColors, styleMap, styleColorCache: {} };

  ctx.resolveStyleColor = (styleId) => {
    if (!styleId) return null;
    if (styleId in ctx.styleColorCache) return ctx.styleColorCache[styleId];
    let id = styleId;
    const seen = new Set();
    let found = null;
    while (id && !seen.has(id)) {
      seen.add(id);
      const s = ctx.styleMap[id];
      if (!s) break;
      const c = colorFromRPr(s.rPr, ctx);
      if (c) { found = c; break; }
      id = s.basedOn;
    }
    ctx.styleColorCache[styleId] = found;
    return found;
  };

  return ctx;
}

// Read a color out of an <w:rPr>, resolving theme colors when needed.
function colorFromRPr(rPr, ctx) {
  const cEl = firstChildNS(rPr, 'color');
  if (!cEl) return null;
  const val = cEl.getAttributeNS(W, 'val');
  if (val && val.toLowerCase() !== 'auto') return '#' + val;
  const themeColor = cEl.getAttributeNS(W, 'themeColor');
  if (themeColor) {
    const slot = THEME_SLOT[themeColor];
    const hex = slot && ctx.themeColors[slot];
    if (hex) return '#' + hex;
  }
  return null;
}

/* ------------------------------ apply to HTML ----------------------------- */

function applyFormatting(html, paragraphs, tables) {
  const container = document.createElement('div');
  container.innerHTML = html;

  applyBlockStyles(container, paragraphs);
  applyTableStyles(container, tables);

  return container.innerHTML;
}

function applyBlockStyles(container, paragraphs) {
  const blocks = Array.from(
    container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li')
  ).filter((el) => !el.closest('table'));

  let cursor = 0;
  blocks.forEach((block) => {
    const text = normalize(block.textContent);
    if (!text) return;

    // Match this block to the next XML paragraph with the same text.
    let idx = -1;
    for (let k = cursor; k < paragraphs.length; k++) {
      if (paragraphs[k].text === text) { idx = k; break; }
    }
    if (idx === -1) {
      // Tolerate minor whitespace/character differences.
      for (let k = cursor; k < paragraphs.length; k++) {
        const pt = paragraphs[k].text;
        if (pt && (pt.startsWith(text) || text.startsWith(pt))) { idx = k; break; }
      }
    }
    if (idx === -1) return;

    const info = paragraphs[idx];
    cursor = idx + 1;

    if (info.align) block.style.textAlign = info.align;
    if (info.color) block.style.color = info.color;
    if (info.highlight) block.style.backgroundColor = info.highlight;
  });
}

function applyTableStyles(container, tables) {
  const tableEls = container.querySelectorAll('table');
  tableEls.forEach((table, ti) => {
    table.style.borderCollapse = 'collapse';
    if (!table.style.width) table.style.width = '100%';

    const rows = tables[ti] || [];
    const trs = table.querySelectorAll('tr');

    trs.forEach((tr, ri) => {
      const cellsData = rows[ri] || [];
      const cells = tr.querySelectorAll('td, th');

      cells.forEach((cell, ci) => {
        if (!cell.style.border) cell.style.border = '1px solid #cccccc';
        if (!cell.style.padding) cell.style.padding = '8px';

        const data = cellsData[ci];
        if (!data) return;

        // Cell shading wins for background; otherwise use highlight if any.
        if (data.fill) cell.style.backgroundColor = data.fill;
        else if (data.highlight) cell.style.backgroundColor = data.highlight;

        if (data.color) cell.style.color = data.color;
        if (data.align) cell.style.textAlign = data.align;
      });
    });
  });
}

/* -------------------------------- helpers --------------------------------- */

// First direct child element with the given local name in the W namespace.
function firstChildNS(parent, localName) {
  return firstChildNSns(parent, W, localName);
}

function firstChildNSns(parent, ns, localName) {
  if (!parent) return null;
  for (let i = 0; i < parent.childNodes.length; i++) {
    const node = parent.childNodes[i];
    if (node.nodeType === 1 && node.namespaceURI === ns && node.localName === localName) {
      return node;
    }
  }
  return null;
}

function valOf(el) {
  return el ? el.getAttributeNS(W, 'val') : null;
}

function pStyleId(p) {
  return valOf(firstChildNS(firstChildNS(p, 'pPr'), 'pStyle'));
}

// Concatenated visible text of a node (w:t text only).
function getText(node) {
  let out = '';
  const texts = node.getElementsByTagNameNS(W, 't');
  for (let i = 0; i < texts.length; i++) out += texts[i].textContent;
  return out;
}

function getAlign(p) {
  const jc = firstChildNS(firstChildNS(p, 'pPr'), 'jc');
  if (!jc) return null;
  const v = jc.getAttributeNS(W, 'val');
  if (v === 'center') return 'center';
  if (v === 'right' || v === 'end') return 'right';
  if (v === 'both' || v === 'distribute') return 'justify';
  if (v === 'left' || v === 'start') return 'left';
  return null;
}

function isInsideTable(node) {
  let n = node.parentNode;
  while (n) {
    if (n.namespaceURI === W && n.localName === 'tbl') return true;
    n = n.parentNode;
  }
  return false;
}

function normalize(str) {
  return (str || '').replace(/\s+/g, ' ').trim();
}
